import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CalendarX, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import useSiteInfo, { telHref } from "@/hooks/useSiteInfo";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const KINDS = {
  termin: { title: "Termin absagen", noun: "Termin", btn: "Ja, Termin absagen", keep: "Termin behalten", rule: "bis 24 Stunden vor dem Termin" },
  anfrage: { title: "Anfrage abbrechen", noun: "Anfrage", btn: "Ja, Anfrage abbrechen", keep: "Anfrage behalten", rule: "solange sie noch nicht bestätigt ist" },
  rueckruf: { title: "Rückrufbitte abbrechen", noun: "Rückrufbitte", btn: "Ja, Rückrufbitte abbrechen", keep: "Rückrufbitte behalten", rule: "solange sie noch nicht bestätigt ist" },
};

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[#E5E5E5] last:border-0">
    <span className="text-[#888B8D]">{label}</span>
    <span className="font-bold text-black text-right whitespace-pre-line" data-testid={testid}>{value}</span>
  </div>
);

const Message = ({ icon: Icon, color, title, text, testid }) => (
  <div className="bg-white p-8 flex items-start gap-4" data-testid={testid}>
    <Icon className={`w-8 h-8 flex-shrink-0 ${color}`} />
    <div>
      <h2 className="text-xl font-bold text-black mb-1">{title}</h2>
      <p className="text-[#888B8D]">{text}</p>
    </div>
  </div>
);

const Details = ({ info, k, busy, onCancel, site }) => (
  <div className="bg-white p-8 space-y-6" data-testid="cancel-details">
    <div>
      <Row label="Status" value={info.status_label} testid="cancel-status" />
      {info.rows.map((r) => <Row key={r.label} label={r.label} value={r.value} testid={`cancel-row-${r.label.toLowerCase()}`} />)}
    </div>
    {info.cancellable ? (
      <div className="flex flex-col sm:flex-row gap-3">
        <Button onClick={onCancel} disabled={busy} className="flex-1 bg-black text-[#EFDF00] hover:bg-[#222] font-bold py-6" data-testid="cancel-confirm-btn">
          {busy ? "Wird abgebrochen ..." : k.btn}
        </Button>
        <Link to="/" className="flex-1">
          <Button variant="outline" className="w-full py-6 border-black text-black" data-testid="cancel-keep-btn">{k.keep}</Button>
        </Link>
      </div>
    ) : (
      <p className="text-[#888B8D]" data-testid="cancel-not-possible">
        {info.status === "cancelled" ? `Diese ${k.noun === "Termin" ? "Buchung" : k.noun} ist bereits abgebrochen.` : `Online abbrechen ist nur ${k.rule} möglich.`}{" "}
        Fragen? <a href={telHref(site.company_phone)} className="underline text-black">{site.company_phone}</a>
      </p>
    )}
  </div>
);

export default function CancelAppointmentPage() {
  const { token } = useParams();
  const site = useSiteInfo();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cancel/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link ungültig"));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cancel/${token}`);
      setDone(r.data.message);
    } catch (e) {
      setError(e.response?.data?.detail || "Abbrechen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const k = KINDS[info?.kind] || { ...KINDS.termin, title: "Abbrechen" };
  let content;
  if (error) {
    content = <Message icon={AlertCircle} color="text-red-600" title="Abbrechen nicht möglich" text={`${error}. Bitte rufen Sie uns an: ${site.company_phone}`} testid="cancel-error" />;
  } else if (done) {
    content = <Message icon={CheckCircle} color="text-green-600" title="Erfolgreich abgebrochen" text={`${done} Sie erhalten eine Bestätigung per E-Mail.`} testid="cancel-success" />;
  } else if (!info) {
    content = <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#888B8D]" /></div>;
  } else {
    content = <Details info={info} k={k} busy={busy} onCancel={cancel} site={site} />;
  }

  return (
    <div className="min-h-screen bg-[#F2F2F2]" data-testid="cancel-appointment-page">
      <Header />
      <section className="py-16">
        <div className="max-w-xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-3">
            <CalendarX className="w-8 h-8 text-black" />
            <h1 className="text-4xl font-bold text-black" data-testid="cancel-title">{k.title}</h1>
          </div>
          <p className="text-[#888B8D] mb-8">Bitte prüfen Sie die Angaben, bevor Sie abbrechen.</p>
          {content}
        </div>
      </section>
      <Footer />
    </div>
  );
}
